/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultMark } from './utils/getDefaultMark';

interface Parameters {
  commodities: number[];
  calendar: number[];
  indexStart: number;
  indexEnd: number;
};
type SourceId = "slide_21" | "slide_21_index" | "slide_21_price";

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResultChartCommodities$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapResultChartIndex$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapResultTable$ = new BehaviorSubject<OLAPResult | null>(null);
  public sources$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(300))
      .subscribe(async (parameters) => {
        if (parameters) {
          this.readyState = ComponentContextReadyState.Loading;
          this.loading$.next(true);
          this.olapResultChartCommodities$.next(null);
          this.olapResultChartIndex$.next(null);
          this.olapResultTable$.next(null);

          const olapResultChartCommoditiesPromise = this.query(parameters, "slide_21");
          const olapResultChartIndexPromise = this.query(parameters, "slide_21_index");
          const olapResultTablePromise = this.query(parameters, "slide_21_price");

          const [olapResultChartCommoities, olapResultChartIndex, olapResultTable] = await Promise.all([
            olapResultChartCommoditiesPromise, 
            olapResultChartIndexPromise, 
            olapResultTablePromise
          ]);

          this.olapResultChartCommodities$.next(olapResultChartCommoities);
          this.olapResultChartIndex$.next(olapResultChartIndex);
          this.olapResultTable$.next(olapResultTable);
          this.readyState = ComponentContextReadyState.Loaded;
          this.loading$.next(false);
        }
      });
  }

  private async _initDefaultMark() {
    const { dataflowService } = this.options;
    const commoditiesParam = dataflowService.getParameter("@commodities") as MultiValueParameter;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const indexStartParam = dataflowService.getParameter("@index_start") as SingleValueParameter;
    const indexEndParam = dataflowService.getParameter("@index_end") as SingleValueParameter;

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    const { calendar } = await getDefaultMark(dictionariesService);
    calendarParam.value = calendar;

    this._parameters.next({
      commodities: commoditiesParam.value as number[],
      calendar: calendarParam.value as number[],
      indexEnd: indexEndParam.value as number,
      indexStart: indexStartParam.value as number,
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar") as number[];
    const indexStart = command.values.get("@index_start") as number;
    const indexEnd = command.values.get("@index_end") as number;
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (calendar !== undefined) {
        parameters.calendar = calendar;
      }
      if (indexStart !== undefined) {
        parameters.indexStart = indexStart;
      }

      if (indexEnd !== undefined) {
        parameters.indexEnd = indexEnd;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters, sourceId: SourceId) {
    const { calendar, commodities, indexStart, indexEnd } = parameters;
    const units = [sourceId === "slide_21_price" ? 55 : 2];
    const { dataSourcesService } = this.options;
    
    const sourcesInput = await Promise.all(
      commodities.map(commodity => 
        dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodity)
      )
    );
    const sources = uniq(sourcesInput.join(', ').split(', ')).filter(Boolean).join(', ');
    this.sources$.next(sources);

    const cubeSlideDef = await dataService.getCubeDefinition(sourceId);
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_calendar"]!["defaultValue"] = calendar.join(',');
    if (sourceId !== "slide_21_price") {
      cubeParameters["p_index_start_date"]!["defaultValue"] = indexStart;
      cubeParameters["p_index_end_date"]!["defaultValue"] = indexEnd;
    }

    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          sourceId === "slide_21_index" ? null : { path: "commodities.default", selection: commodities, selectionMode: SelectionMode.Include },
          { path: "units.default", selection: units, selectionMode: SelectionMode.Include },
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ].filter(Boolean),
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);
    delete olapResult.data.aggregatedSelection['units.default'];
    olapResult.data.rows.forEach(row => delete row.selection['units.default']);
    return olapResult;
  }
}
